import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { templatesAPI, userTemplatesAPI, vehiclesAPI } from "@/lib/api";
import { HeaderIcon } from "@/lib/assets";
import { toast } from "sonner";
import {
  ArrowLeft,
  Loader2,
  ClipboardList,
  Wrench,
  CheckCircle,
  ChevronRight,
  Calendar,
  Gauge,
  Truck,
  Zap,
  Mountain,
  Car,
  User,
  Plus,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const TEMPLATE_ICONS = {
  severe_duty_4x4: Zap,
  overland_touring: Mountain,
  weekend_warrior: Car,
};

const TEMPLATE_COLORS = {
  severe_duty_4x4: "text-red-500 bg-red-500/10 border-red-500/30",
  overland_touring: "text-amber-500 bg-amber-500/10 border-amber-500/30",
  weekend_warrior: "text-green-500 bg-green-500/10 border-green-500/30",
};

export default function MaintenanceTemplates() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [vehicle, setVehicle] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [customTemplates, setCustomTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateDetails, setTemplateDetails] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isCustomTemplate, setIsCustomTemplate] = useState(false);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const [vehicleRes, templatesRes, customRes] = await Promise.all([
        vehiclesAPI.get(id),
        templatesAPI.list(),
        userTemplatesAPI.list(),
      ]);
      setVehicle(vehicleRes.data);
      setTemplates(templatesRes.data.templates);
      setCustomTemplates(customRes.data.templates || []);
    } catch (error) {
      toast.error("Failed to load data");
      navigate(`/vehicles/${id}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTemplate = async (templateId, isCustom = false) => {
    try {
      const api = isCustom ? userTemplatesAPI : templatesAPI;
      const { data } = await api.get(templateId);
      setSelectedTemplate(templateId);
      setTemplateDetails(data);
      setIsCustomTemplate(isCustom);
      setShowConfirm(true);
    } catch (error) {
      toast.error("Failed to load template details");
    }
  };

  const handleApplyTemplate = async () => {
    if (!selectedTemplate) return;
    
    setApplying(true);
    setShowConfirm(false);
    
    try {
      const api = isCustomTemplate ? userTemplatesAPI : templatesAPI;
      const { data } = await api.apply(id, selectedTemplate);
      toast.success(`Created ${data.created_services} service records`);
      navigate(`/vehicles/${id}/maintenance`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to apply template");
    } finally {
      setApplying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card/50 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16 gap-4">
            <Link
              to={`/vehicles/${id}`}
              data-testid="back-btn"
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-3">
              <HeaderIcon />

              <div>
                <h1 className="text-lg font-bold text-foreground">
                  Maintenance Templates
                </h1>
                <p className="text-xs text-muted-foreground">{vehicle?.nickname}</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          {/* Intro */}
          <Card className="card-tactical bg-zinc-950/50 border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-sm flex items-center justify-center flex-shrink-0">
                  <Truck className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-medium text-foreground">Quick Setup for {vehicle?.nickname}</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Choose a maintenance schedule template based on your vehicle's use case. 
                    This will create initial service records with recommended intervals.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Templates Tabs */}
          <Tabs defaultValue="system" className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-zinc-900 border border-zinc-800">
              <TabsTrigger value="system" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                System Templates
              </TabsTrigger>
              <TabsTrigger value="custom" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                My Templates ({customTemplates.length})
              </TabsTrigger>
            </TabsList>

            {/* System Templates */}
            <TabsContent value="system" className="mt-4 space-y-4">
              {templates.map((template) => {
                const Icon = TEMPLATE_ICONS[template.id] || Wrench;
                const colorClass = TEMPLATE_COLORS[template.id] || "text-zinc-500 bg-zinc-500/10 border-zinc-500/30";
                
                return (
                  <Card 
                    key={template.id} 
                    className="card-tactical bg-zinc-950/50 border-zinc-800 hover:border-zinc-700 transition-colors cursor-pointer"
                    onClick={() => handleSelectTemplate(template.id, false)}
                    data-testid={`template-${template.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 rounded-sm flex items-center justify-center border ${colorClass}`}>
                            <Icon className="w-6 h-6" />
                          </div>
                          <div>
                            <h3 className="font-bold text-foreground">{template.name}</h3>
                            <p className="text-sm text-muted-foreground">{template.description}</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {template.service_count} service items
                            </p>
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-muted-foreground" />
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </TabsContent>

            {/* Custom Templates */}
            <TabsContent value="custom" className="mt-4 space-y-4">
              {customTemplates.length === 0 ? (
                <Card className="card-tactical bg-zinc-950/50 border-zinc-800">
                  <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                    <div className="w-12 h-12 bg-zinc-900 rounded-sm flex items-center justify-center mb-4">
                      <User className="w-6 h-6 text-zinc-600" />
                    </div>
                    <h3 className="text-lg font-bold text-foreground mb-2">
                      No Custom Templates
                    </h3>
                    <p className="text-muted-foreground mb-4 text-sm">
                      Create your own reusable maintenance schedules
                    </p>
                    <Button
                      onClick={() => navigate("/templates")}
                      data-testid="create-template-link"
                      className="btn-tactical bg-primary text-primary-foreground hover:bg-primary/90"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Create Template
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <>
                  {customTemplates.map((template) => (
                    <Card 
                      key={template.id} 
                      className="card-tactical bg-zinc-950/50 border-zinc-800 hover:border-zinc-700 transition-colors cursor-pointer"
                      onClick={() => handleSelectTemplate(template.id, true)}
                      data-testid={`custom-template-${template.id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-sm flex items-center justify-center border border-blue-500/30 bg-blue-500/10 text-blue-500">
                              <User className="w-6 h-6" />
                            </div>
                            <div>
                              <h3 className="font-bold text-foreground">{template.name}</h3>
                              {template.description && (
                                <p className="text-sm text-muted-foreground">{template.description}</p>
                              )}
                              <p className="text-xs text-muted-foreground mt-1">
                                {template.service_count || template.services?.length || 0} service items
                              </p>
                            </div>
                          </div>
                          <ChevronRight className="w-5 h-5 text-muted-foreground" />
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  <div className="text-center pt-2">
                    <Button
                      variant="ghost"
                      onClick={() => navigate("/templates")}
                      className="text-primary hover:text-primary/80"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Manage My Templates
                    </Button>
                  </div>
                </>
              )}
            </TabsContent>
          </Tabs>

          {/* Skip Option */}
          <div className="text-center pt-4">
            <Button
              variant="ghost"
              onClick={() => navigate(`/vehicles/${id}`)}
              className="text-muted-foreground hover:text-foreground"
            >
              Skip - I'll set up maintenance manually
            </Button>
          </div>
        </div>
      </main>

      {/* Confirmation Dialog */}
      <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
        <AlertDialogContent className="bg-zinc-950 border-zinc-800 max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>Apply {templateDetails?.name}?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4">
                <p>{templateDetails?.description}</p>
                
                <div className="bg-zinc-900 rounded-sm p-3 max-h-64 overflow-y-auto">
                  <p className="text-xs text-muted-foreground uppercase mb-2">Services to be created:</p>
                  <div className="space-y-2">
                    {templateDetails?.services?.map((service, index) => (
                      <div key={index} className="flex items-center justify-between text-sm">
                        <span className="text-foreground">{service.title}</span>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          {service.interval_miles && (
                            <span className="flex items-center gap-1">
                              <Gauge className="w-3 h-3" />
                              {service.interval_miles.toLocaleString()} mi
                            </span>
                          )}
                          {service.interval_months && (
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {service.interval_months} mo
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                <p className="text-xs text-muted-foreground">
                  Current mileage ({vehicle?.mileage_current?.toLocaleString() || 0} mi) will be used as the baseline.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-900 border-zinc-800">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleApplyTemplate}
              disabled={applying}
              data-testid="confirm-apply-btn"
              className="bg-primary hover:bg-primary/90"
            >
              {applying ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              Apply Template
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
